import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Building2, 
  MapPin, 
  Calendar,
  Mail,
  Phone,
  Users,
  UserCheck,
  Loader2,
  AlertCircle,
  FileText,
  Globe,
  Hash,
  Pencil,
  Check,
  X,
  Shield,
  CreditCard,
  Briefcase,
  Flag,
  Home,
  User,
  Percent,
  Star,
  Power,
  PowerOff,
  Trash2,
  AlertTriangle,
  Upload,
  Download,
  Eye,
  ToggleLeft,
  ToggleRight,
  Info,
  CheckCircle,
  ShieldCheck,
  Tag,
  CalendarDays,
  BookUser,
  ShoppingBag,
  Plus
} from 'lucide-react';
import MailScansSection from '../../components/superadmin/MailScansSection';
import AdminDocVaultTab from '../../components/superadmin/AdminDocVaultTab';
import AdminContactsTab from '../../components/superadmin/AdminContactsTab';
import AdminDocumentRoomTab from '../../components/superadmin/AdminDocumentRoomTab';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { formatLongDate } from '@/utils/dateFormat';
import { getStatusStyle } from '@/lib/orderStatusStyles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_STATUS_STYLES = {
  active: { bg: 'bg-green-500/15', text: 'text-green-400', label: 'Active' },
  expiring_soon: { bg: 'bg-amber-500/15', text: 'text-amber-400', label: 'Expiring Soon' },
  expired: { bg: 'bg-red-500/15', text: 'text-red-400', label: 'Expired' },
  not_set: { bg: 'bg-gray-500/15', text: 'text-gray-500', label: 'Not Set' },
  not_added: { bg: 'bg-gray-500/15', text: 'text-gray-500', label: 'Not Added' },
};

const ServiceStatusBadge = ({ status }) => {
  const style = SERVICE_STATUS_STYLES[status] || SERVICE_STATUS_STYLES.not_set;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text}`} data-testid={`status-badge-${status}`}>
      {style.label}
    </span>
  );
};

// Service Date Card — renders MF/RA date states per priority logic:
// - source="order" + expiry: "Expires: [date]" + status badge
// - source="order" + no expiry: "Active — expiry date set on completion"
// - source="manual" + locked: "Expires: [date]" + status badge + unlock note
// - source=null: editable date picker
const ServiceDateCard = ({ label, status, expiry, source, locked, dateValue, onDateChange, onSave, saving, testId }) => {
  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };

  const isOrderSource = source === 'order';
  const isManualLocked = source === 'manual' && locked;
  const hasExpiry = !!expiry;

  return (
    <div className="bg-[#0D1117] border border-gray-700/50 rounded-xl p-5" data-testid={`service-date-card-${testId}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white">{label}</h3>
        <ServiceStatusBadge status={status || 'not_set'} />
      </div>

      {isOrderSource && hasExpiry ? (
        <div className="space-y-2" data-testid={`service-date-order-expiry-${testId}`}>
          <div className="text-xs text-gray-500 uppercase tracking-wider">Managed by order</div>
          <div className="text-white text-base font-semibold" data-testid={`service-date-expiry-${testId}`}>
            Expires: {formatDate(expiry)}
          </div>
          <p className="text-gray-500 text-xs">Auto-renewed when the underlying order renews.</p>
        </div>
      ) : isOrderSource ? (
        <div className="space-y-2" data-testid={`service-date-order-pending-${testId}`}>
          <div className="text-xs text-gray-500 uppercase tracking-wider">Managed by order</div>
          <div className="text-white text-base font-semibold" data-testid={`service-date-expiry-${testId}`}>
            Active — expiry date set on completion
          </div>
          <p className="text-gray-500 text-xs">Expiry will auto-populate once the order completes.</p>
        </div>
      ) : isManualLocked ? (
        <div className="space-y-2" data-testid={`service-date-locked-${testId}`}>
          <div className="text-xs text-gray-500 uppercase tracking-wider">Manually Set</div>
          <div className="text-white text-base font-semibold" data-testid={`service-date-expiry-${testId}`}>
            Expires: {formatDate(expiry)}
          </div>
          <p className="text-gray-500 text-xs">This date is locked. It will update automatically when an order is placed.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid={`service-date-editable-${testId}`}>
          <div className="text-xs text-gray-500 uppercase tracking-wider">Not Added</div>
          <input
            type="date"
            value={dateValue}
            onChange={(e) => onDateChange(e.target.value)}
            className="w-full bg-[#161B22] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00E87A]"
            data-testid={`service-date-input-${testId}`}
          />
          <button
            onClick={onSave}
            disabled={saving || !dateValue}
            className="w-full px-3 py-2 bg-[#00E87A] text-black text-sm font-medium rounded-lg hover:bg-[#00D46F] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid={`service-date-save-${testId}`}
          >
            {saving ? 'Saving…' : 'Save Due Date'}
          </button>
          <p className="text-gray-500 text-xs">Once saved, this date is locked and can only be changed via an order.</p>
        </div>
      )}
    </div>
  );
};

const ENTITY_TYPE_LABELS = {
  us_llc: 'LLC',
  us_c_corp: 'C-Corp',
  us_s_corp: 'S-Corp',
  us_nonprofit: 'Nonprofit',
  us_partnership: 'Partnership',
  us_sole_prop: 'Sole Proprietorship'
};

// Base tabs (always shown)
const BASE_TABS = [
  { id: 'info', label: 'Company Info', icon: Building2 },
  { id: 'members', label: 'Officers', icon: Users },
  { id: 'owners', label: 'Beneficial Owners', icon: UserCheck },
  { id: 'docvault', label: 'DocVault', icon: ShieldCheck },
  { id: 'contacts', label: 'Parties', icon: BookUser },
  { id: 'docroom', label: 'Data Room', icon: FileText },
  { id: 'orders', label: 'Orders', icon: ShoppingBag },
];

const DOCVAULT_TAGS = [
  { value: 'tax_filing', label: 'Tax Filing' },
  { value: 'legal_agreement', label: 'Legal Agreement' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'annual_report', label: 'Annual Report' },
  { value: 'other', label: 'Other' },
];

// KYC Status Badge Component
const KYCBadge = ({ status }) => {
  const statusConfig = {
    verified: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Verified' },
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Pending' },
    rejected: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Rejected' }
  };
  
  const config = statusConfig[status] || statusConfig.pending;
  
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Shield className="w-3 h-3 mr-1" />
      {config.label}
    </span>
  );
};

// Yes/No Badge Component
const YesNoBadge = ({ value, yesLabel = 'Yes', noLabel = 'No' }) => {
  if (value) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#00E87A]/20 text-[#00E87A]">
        {yesLabel}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-600/50 text-gray-400">
      {noLabel}
    </span>
  );
};

// KYC Action Section — admin Verify / Reject / Re-verify controls
const MemberKYCActions = ({ member, companyId, token, onUpdate, formatDate }) => {
  const [mode, setMode] = useState('idle'); // idle | rejecting
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const status = member.kyc_status || 'pending';

  const callKyc = async (action, body = null) => {
    setSubmitting(true);
    setError('');
    try {
      await axios.post(
        `${API}/admin/companies/${companyId}/members/${member.id}/kyc-${action}`,
        body || {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMode('idle');
      setReason('');
      if (onUpdate) await onUpdate();
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${action} KYC`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = () => callKyc('verify');
  const handleReject = () => {
    const r = reason.trim();
    if (!r) { setError('Rejection reason is required'); return; }
    callKyc('reject', { reason: r });
  };

  if (status === 'verified') {
    return (
      <div className="py-3 border-t border-gray-700/50" data-testid={`member-kyc-section-${member.id}`}>
        <div className="flex items-center gap-2 text-sm text-green-400">
          <CheckCircle className="w-4 h-4" />
          <span>
            Verified
            {member.kyc_verified_by_name ? ` by ${member.kyc_verified_by_name}` : ''}
            {member.kyc_verified_at ? ` on ${formatDate(member.kyc_verified_at)}` : ''}
          </span>
        </div>
      </div>
    );
  }

  if (status === 'rejected') {
    return (
      <div className="py-3 border-t border-gray-700/50 space-y-2" data-testid={`member-kyc-section-${member.id}`}>
        {member.kyc_rejection_reason && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-300">
                <span className="font-medium">Rejection reason:</span> {member.kyc_rejection_reason}
                {member.kyc_verified_by_name && (
                  <div className="text-xs text-red-400/70 mt-1">
                    by {member.kyc_verified_by_name}
                    {member.kyc_verified_at ? ` · ${formatDate(member.kyc_verified_at)}` : ''}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {error && <div className="text-xs text-red-400">{error}</div>}
        <button
          onClick={handleVerify}
          disabled={submitting}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#00E87A]/15 text-[#00E87A] hover:bg-[#00E87A]/25 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
          data-testid={`kyc-reverify-btn-${member.id}`}
        >
          <CheckCircle className="w-3.5 h-3.5" />
          {submitting ? 'Re-verifying…' : 'Re-verify'}
        </button>
      </div>
    );
  }

  // Pending
  return (
    <div className="py-3 border-t border-gray-700/50 space-y-2" data-testid={`member-kyc-section-${member.id}`}>
      {error && <div className="text-xs text-red-400">{error}</div>}
      {mode === 'rejecting' ? (
        <div className="space-y-2">
          <label className="text-xs text-gray-400 block">Rejection reason</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason visible to admin only"
            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-red-500/60"
            data-testid={`kyc-reject-reason-${member.id}`}
            autoFocus
          />
          <div className="flex items-center gap-2">
            <button
              onClick={handleReject}
              disabled={submitting || !reason.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 text-red-300 hover:bg-red-500/30 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
              data-testid={`kyc-reject-confirm-${member.id}`}
            >
              <X className="w-3.5 h-3.5" />
              {submitting ? 'Rejecting…' : 'Confirm Reject'}
            </button>
            <button
              onClick={() => { setMode('idle'); setReason(''); setError(''); }}
              disabled={submitting}
              className="px-3 py-1.5 text-xs text-gray-400 hover:text-white transition-colors"
              data-testid={`kyc-reject-cancel-${member.id}`}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={handleVerify}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#00E87A]/15 text-[#00E87A] hover:bg-[#00E87A]/25 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
            data-testid={`kyc-verify-btn-${member.id}`}
          >
            <Check className="w-3.5 h-3.5" />
            {submitting ? 'Verifying…' : 'Verify'}
          </button>
          <button
            onClick={() => setMode('rejecting')}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500/15 text-red-300 hover:bg-red-500/25 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
            data-testid={`kyc-reject-btn-${member.id}`}
          >
            <X className="w-3.5 h-3.5" />
            Reject
          </button>
        </div>
      )}
    </div>
  );
};

// Formation Documents — admin view of self-registered formation docs.
// Permanently visible (not just in the Pending Review modal) so admins can
// reference them after approval. Hidden for StartEase-formed companies.
const DOC_TYPE_LABELS = {
  operating_agreement: 'Operating Agreement',
  articles_of_organization: 'Formation Certificate',
  ein_letter: 'EIN Letter',
  corporate_bylaws: 'Corporate Bylaws',
};

const FormationDocumentsSection = ({ companyId, token }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/companies/${companyId}/documents`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { document_type: undefined }, // listing endpoint
        });
        if (cancelled) return;
        const formationDocs = (res.data?.documents || []).filter(
          (d) => d.document_category === 'formation' && d.is_current
        );
        setDocs(formationDocs);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Failed to load documents');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, token]);

  const fmt = (d) => {
    if (!d) return '-';
    try {
      return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return '-'; }
  };

  return (
    <div
      className="lg:col-span-2 bg-gray-800/30 border border-gray-700/50 rounded-xl p-6"
      data-testid="formation-documents-section"
    >
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <FileText className="w-5 h-5 text-[#00E87A]" />
        Formation Documents
      </h2>

      {loading && (
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {!loading && !error && docs.length === 0 && (
        <p className="text-gray-500 text-sm" data-testid="formation-docs-empty">
          No formation documents uploaded
        </p>
      )}

      {!loading && !error && docs.length > 0 && (
        <div className="space-y-2" data-testid="formation-docs-list">
          {docs.map((d) => (
            <div
              key={d.id}
              className="flex items-center justify-between bg-gray-900/40 border border-gray-700 rounded-lg p-3"
              data-testid={`formation-doc-${d.document_type}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-[#00E87A]/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-[#00E87A]" />
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium">
                    {DOC_TYPE_LABELS[d.document_type] || d.document_type}
                  </p>
                  <p className="text-gray-500 text-xs">
                    Uploaded {fmt(d.uploaded_at)} · v{d.version || 1}
                  </p>
                </div>
              </div>
              <a
                href={d.presigned_url || d.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-gray-300 border border-gray-600 rounded hover:bg-gray-700"
                data-testid={`formation-doc-view-${d.document_type}`}
              >
                <Eye className="w-3.5 h-3.5" /> View
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Member Card Component - Detailed View
// MEMBER_TITLES: entity-aware dropdown options (kept in sync with the client
// CompanyDetailPage.jsx + backend US_TITLES_BY_ENTITY).
const MEMBER_TITLES = {
  us_llc: ['Member', 'Manager', 'Member / Manager'],
  us_c_corp: ['Director', 'Officer', 'Director & Officer', 'Shareholder'],
  us_s_corp: ['Director', 'Officer', 'Director & Officer', 'Shareholder'],
  us_sole_proprietorship: ['Owner', 'Sole Proprietor'],
};

const MemberCard = ({ member, formatDate, companyId, token, onUpdate, entityType }) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(member.us_title || '');
  const [savingTitle, setSavingTitle] = useState(false);
  const [titleError, setTitleError] = useState('');

  const titleOptions = React.useMemo(() => {
    const base = MEMBER_TITLES[entityType] || MEMBER_TITLES.us_llc;
    // Always include the current value so legacy / unknown values are not silently dropped
    return member.us_title && !base.includes(member.us_title)
      ? [member.us_title, ...base]
      : base;
  }, [entityType, member.us_title]);

  const saveTitle = async () => {
    if (titleValue === (member.us_title || '')) {
      setIsEditingTitle(false);
      return;
    }
    setSavingTitle(true);
    setTitleError('');
    try {
      await axios.put(
        `${API}/companies/${companyId}/members/${member.id}`,
        { us_title: titleValue || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsEditingTitle(false);
      if (onUpdate) await onUpdate();
    } catch (err) {
      setTitleError(err.response?.data?.detail || 'Failed to update title');
    } finally {
      setSavingTitle(false);
    }
  };

  const cancelTitleEdit = () => {
    setTitleValue(member.us_title || '');
    setIsEditingTitle(false);
    setTitleError('');
  };

  const formatSSN = (ssn) => {
    if (!ssn) return null;
    return `****${ssn}`;
  };

  const formatAddress = (address) => {
    if (!address) return null;
    const parts = [address.street, address.city, address.state, address.zip_code].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  return (
    <div 
      className="bg-[#0D1117] border border-gray-700/50 rounded-xl p-6 hover:border-[#00E87A]/30 transition-all"
      data-testid={`member-card-${member.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center">
            <User className="w-6 h-6 text-gray-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white" data-testid={`member-name-${member.id}`}>
              {member.full_legal_name}
            </h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {isEditingTitle ? (
                <div className="flex items-center gap-2" data-testid={`member-title-editor-${member.id}`}>
                  <select
                    value={titleValue}
                    onChange={(e) => setTitleValue(e.target.value)}
                    disabled={savingTitle}
                    className="px-2 py-1 bg-[#161B22] border border-gray-700 rounded text-xs text-white focus:outline-none focus:border-[#00E87A]"
                    data-testid={`member-title-select-${member.id}`}
                  >
                    <option value="">— Select title —</option>
                    {titleOptions.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={saveTitle}
                    disabled={savingTitle}
                    className="px-2 py-1 text-xs bg-[#00E87A] text-gray-900 rounded font-medium hover:opacity-90 disabled:opacity-50"
                    data-testid={`member-title-save-${member.id}`}
                  >
                    {savingTitle ? '...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelTitleEdit}
                    disabled={savingTitle}
                    className="px-2 py-1 text-xs bg-gray-700 text-gray-200 rounded hover:bg-gray-600 disabled:opacity-50"
                    data-testid={`member-title-cancel-${member.id}`}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  {member.us_title ? (
                    <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">
                      {member.us_title}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 bg-gray-700/40 text-gray-400 text-xs rounded italic">
                      No title
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => { setTitleValue(member.us_title || ''); setIsEditingTitle(true); }}
                    className="p-1 text-gray-500 hover:text-[#00E87A] transition-colors"
                    title="Edit title"
                    data-testid={`member-title-edit-${member.id}`}
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                </>
              )}
              {member.is_primary_contact && (
                <span className="px-2 py-0.5 bg-[#00E87A]/20 text-[#00E87A] text-xs rounded flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  Primary Contact
                </span>
              )}
              {titleError && (
                <span className="text-xs text-red-400" data-testid={`member-title-error-${member.id}`}>
                  {titleError}
                </span>
              )}
              {!isEditingTitle && member.title_updated_by_admin_at && (
                <span
                  className="text-[10px] text-blue-300/70"
                  data-testid={`member-title-admin-audit-${member.id}`}
                >
                  Updated by Admin on {formatLongDate(member.title_updated_by_admin_at)}
                </span>
              )}
            </div>
          </div>
        </div>
        <KYCBadge status={member.kyc_status} />
      </div>

      {/* KYC Verification Actions (admin) */}
      {companyId && token && (
        <MemberKYCActions
          member={member}
          companyId={companyId}
          token={token}
          onUpdate={onUpdate}
          formatDate={formatDate}
        />
      )}

      {/* Contact Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {member.email && (
          <div className="flex items-center gap-2 text-gray-300">
            <Mail className="w-4 h-4 text-gray-500" />
            <span className="text-sm" data-testid={`member-email-${member.id}`}>{member.email}</span>
          </div>
        )}
        {member.phone && (
          <div className="flex items-center gap-2 text-gray-300">
            <Phone className="w-4 h-4 text-gray-500" />
            <span className="text-sm">{member.phone}</span>
          </div>
        )}
      </div>

      {/* Personal Details */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4 border-t border-gray-700/50">
        {member.date_of_birth && (
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Date of Birth</label>
            <p className="text-gray-300 text-sm">{member.date_of_birth}</p>
          </div>
        )}
        {member.nationality && (
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Nationality</label>
            <p className="text-gray-300 text-sm">{member.nationality}</p>
          </div>
        )}
        {member.country_of_residence && (
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Country of Residence</label>
            <p className="text-gray-300 text-sm">{member.country_of_residence}</p>
          </div>
        )}
        {(member.ownership_percentage !== null && member.ownership_percentage !== undefined) && (
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Ownership %</label>
            <p className="text-gray-300 text-sm flex items-center gap-1">
              <Percent className="w-3 h-3 text-[#00E87A]" />
              {member.ownership_percentage}%
            </p>
          </div>
        )}
        {(member.units !== null && member.units !== undefined) && (
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Units</label>
            <p className="text-gray-300 text-sm">{member.units}</p>
          </div>
        )}
      </div>

      {/* Status Badges */}
      <div className="flex flex-wrap gap-3 py-4 border-t border-gray-700/50">
        <div>
          <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">US Resident</label>
          <YesNoBadge value={member.is_us_resident} />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">W8 Provided</label>
          <YesNoBadge value={member.w8_provided} />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Primary Contact</label>
          <YesNoBadge value={member.is_primary_contact} />
        </div>
      </div>

      {/* Sensitive Information - Only shown if exists */}
      {(formatSSN(member.ssn_last_four) || member.itin || member.passport_number) && (
        <div className="py-4 border-t border-gray-700/50">
          <label className="text-xs text-gray-500 uppercase tracking-wider block mb-3 flex items-center gap-1">
            <Shield className="w-3 h-3" />
            Sensitive Information
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {formatSSN(member.ssn_last_four) && (
              <div>
                <label className="text-xs text-gray-500 block mb-1">SSN Last 4</label>
                <p className="text-gray-300 text-sm font-mono" data-testid={`member-ssn-${member.id}`}>
                  {formatSSN(member.ssn_last_four)}
                </p>
              </div>
            )}
            {member.itin && (
              <div>
                <label className="text-xs text-gray-500 block mb-1">ITIN</label>
                <p className="text-gray-300 text-sm font-mono">{member.itin}</p>
              </div>
            )}
            {member.passport_number && (
              <div>
                <label className="text-xs text-gray-500 block mb-1">Passport Number</label>
                <p className="text-gray-300 text-sm font-mono">{member.passport_number}</p>
              </div>
            )}
            {member.passport_expiry && (
              <div>
                <label className="text-xs text-gray-500 block mb-1">Passport Expiry</label>
                <p className="text-gray-300 text-sm">{member.passport_expiry}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Passport Document */}
      <div className="py-4 border-t border-gray-700/50" data-testid={`member-passport-section-${member.id}`}>
        <label className="text-xs text-gray-500 uppercase tracking-wider block mb-3 flex items-center gap-1">
          <FileText className="w-3 h-3" />
          Passport Document
        </label>
        {member.passport ? (
          <div className="flex items-center justify-between bg-gray-900/50 border border-gray-700/50 rounded-lg p-3">
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="w-5 h-5 text-[#00E87A] flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-white text-sm truncate" title={member.passport.file_name}>
                  {member.passport.file_name}
                </p>
                <p className="text-gray-500 text-xs">
                  v{member.passport.version || 1}
                  {member.passport.uploaded_at && ` · Uploaded ${formatDate(member.passport.uploaded_at)}`}
                  {member.passport.expiry_date && ` · Expires ${member.passport.expiry_date}`}
                </p>
              </div>
            </div>
            <a
              href={member.passport.presigned_url || member.passport.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-3 flex items-center gap-1.5 px-3 py-1.5 bg-gray-700/50 text-gray-300 text-xs rounded-lg hover:bg-gray-700 hover:text-white transition-colors flex-shrink-0"
              data-testid={`view-passport-${member.id}`}
            >
              <Eye className="w-3 h-3" />
              View
            </a>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-gray-500 text-sm" data-testid={`passport-not-uploaded-${member.id}`}>
            <AlertCircle className="w-4 h-4" />
            Not uploaded
          </div>
        )}
      </div>

      {/* US Address - Only shown if exists */}
      {formatAddress(member.us_address) && (
        <div className="py-4 border-t border-gray-700/50">
          <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2 flex items-center gap-1">
            <Home className="w-3 h-3" />
            US Address
          </label>
          <p className="text-gray-300 text-sm">{formatAddress(member.us_address)}</p>
        </div>
      )}
    </div>
  );
};

const SuperAdminCompanyDetail = () => {
  const { companyId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [members, setMembers] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  
  // BOIR editing state
  const [editingBoir, setEditingBoir] = useState(false);
  const [boirValue, setBoirValue] = useState(false);
  const [savingBoir, setSavingBoir] = useState(false);

  // EIN editing state
  const [editingEin, setEditingEin] = useState(false);
  const [einValue, setEinValue] = useState('');
  const [savingEin, setSavingEin] = useState(false);
  const [editingStateTaxId, setEditingStateTaxId] = useState(false);
  const [stateTaxIdValue, setStateTaxIdValue] = useState('');
  const [savingStateTaxId, setSavingStateTaxId] = useState(false);
  const [savingTaxIds, setSavingTaxIds] = useState(false);

  // Date of Formation editing state
  const [editingDof, setEditingDof] = useState(false);
  const [dofValue, setDofValue] = useState('');
  const [savingDof, setSavingDof] = useState(false);
  
  // BOIR filing required toggle
  const [boirRequired, setBoirRequired] = useState(false);
  const [boirReason, setBoirReason] = useState('');
  const [savingBoirStatus, setSavingBoirStatus] = useState(false);
  const [editingBoirStatus, setEditingBoirStatus] = useState(false);
  
  // BOIR document upload
  const [uploadingBoir, setUploadingBoir] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const boirFileRef = useRef(null);
  
  // BO ID document viewing
  const [viewingDocId, setViewingDocId] = useState(null);
  
  // Mail Scans — active services detection
  const [activeMailServices, setActiveMailServices] = useState([]);
  
  // Action states
  const [actionLoading, setActionLoading] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Service Dates state
  const [mfDueDate, setMfDueDate] = useState('');
  const [raDueDate, setRaDueDate] = useState('');
  const [savingMf, setSavingMf] = useState(false);
  const [savingRa, setSavingRa] = useState(false);
  const [serviceDateSuccess, setServiceDateSuccess] = useState('');
  const [serviceDateError, setServiceDateError] = useState('');

  // State Filing card (Stage 4) — populated from GET /companies/{id}/state-filing.
  // null = inactive (card shows "not available" message). Object when active.
  const [stateFiling, setStateFiling] = useState(null);
  const [stateFilingLoaded, setStateFilingLoaded] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderMessage, setReminderMessage] = useState('');

  const isSuperAdmin = user?.role === 'super_admin';
  const token = localStorage.getItem('auth_token');

  const syncServiceDates = (companyData) => {
    setMfDueDate(companyData?.mail_forwarding_due_date?.slice(0, 10) || '');
    setRaDueDate(companyData?.registered_agent_due_date?.slice(0, 10) || '');
  };

  const handleSendStateFilingReminder = async () => {
    setSendingReminder(true);
    setReminderMessage('');
    try {
      const { data } = await axios.post(
        `${API}/admin/companies/${companyId}/state-filing/send-reminder`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReminderMessage(`Reminder sent to ${data.to}`);
      setTimeout(() => setReminderMessage(''), 4000);
    } catch (err) {
      setReminderMessage(err.response?.data?.detail || 'Failed to send reminder');
      setTimeout(() => setReminderMessage(''), 4000);
    } finally {
      setSendingReminder(false);
    }
  };

  const handleSaveServiceDate = async (service) => {
    const isMf = service === 'mail_forwarding';
    const setter = isMf ? setSavingMf : setSavingRa;
    const dateVal = isMf ? mfDueDate : raDueDate;
    setter(true);
    setServiceDateError('');
    try {
      const payload = {};
      payload[`${service}_due_date`] = dateVal || null;
      await axios.patch(`${API}/admin/companies/${companyId}/service-dates`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServiceDateSuccess(`${isMf ? 'Mail Forwarding' : 'Registered Agent'} date updated`);
      setTimeout(() => setServiceDateSuccess(''), 3000);
      fetchCompanyData();
    } catch (err) {
      setServiceDateError(err.response?.data?.detail || 'Failed to update');
    } finally {
      setter(false);
    }
  };

  useEffect(() => {
    fetchCompanyData();
  }, [companyId]);

  // Fetch orders lazily when the Orders tab is opened
  useEffect(() => {
    if (activeTab !== 'orders' || !companyId) return;
    const fetchOrders = async () => {
      setOrdersLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        const res = await axios.get(`${API}/admin/orders?company_id=${companyId}&limit=200`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrders(res.data.orders || []);
      } catch (err) {
        setOrders([]);
      } finally {
        setOrdersLoading(false);
      }
    };
    fetchOrders();
  }, [activeTab, companyId]);

  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch company, members, owners, and state filing in parallel
      const [companyRes, membersRes, ownersRes, stateFilingRes] = await Promise.all([
        axios.get(`${API}/companies/${companyId}`, { headers }),
        axios.get(`${API}/admin/companies/${companyId}/members`, { headers }).catch(() => ({ data: { members: [] } })),
        axios.get(`${API}/companies/${companyId}/beneficial-owners`, { headers }).catch(() => ({ data: { beneficial_owners: [] } })),
        // 404 = inactive for this state×entity → render the "not available" message
        axios.get(`${API}/companies/${companyId}/state-filing`, { headers }).catch(() => ({ data: null })),
      ]);

      setCompany(companyRes.data);
      syncServiceDates(companyRes.data);
      setBoirValue(companyRes.data.boir_filed || false);
      setBoirRequired(companyRes.data.boir_filing_required || false);
      setBoirReason(companyRes.data.boir_filing_reason || '');
      setMembers(membersRes.data.members || []);
      setOwners(ownersRes.data.beneficial_owners || ownersRes.data.owners || []);
      setStateFiling(stateFilingRes.data);
      setStateFilingLoaded(true);

      // Detect active RA / Mail Forwarding services
      try {
        const svcRes = await axios.get(`${API}/admin/companies/${companyId}/active-mail-services`, { headers });
        setActiveMailServices(svcRes.data.active_services || []);
      } catch { setActiveMailServices([]); }
      setError(null);
    } catch (err) {
      console.error('Error fetching company data:', err);
      setError('Failed to load company details');
    } finally {
      setLoading(false);
    }
  };

  const handleBoirSave = async () => {
    try {
      setSavingBoir(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.put(
        `${API}/companies/${companyId}`,
        { boir_filed: boirValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompany(response.data);
      setEditingBoir(false);
    } catch (err) {
      console.error('Error updating BOIR status:', err);
      alert('Failed to update BOIR status');
    } finally {
      setSavingBoir(false);
    }
  };

  const handleBoirCancel = () => {
    setBoirValue(company?.boir_filed || false);
    setEditingBoir(false);
  };

  // EIN inline edit (admin + super_admin)
  const handleEinSave = async () => {
    try {
      setSavingEin(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.put(
        `${API}/companies/${companyId}`,
        { ein: einValue.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompany(response.data);
      setEditingEin(false);
    } catch (err) {
      console.error('Error updating EIN:', err);
      alert('Failed to update EIN');
    } finally {
      setSavingEin(false);
    }
  };

  const handleEinCancel = () => {
    setEinValue(company?.ein || '');
    setEditingEin(false);
  };

  // State Tax ID inline edit (admin + super_admin)
  const handleStateTaxIdSave = async () => {
    try {
      setSavingStateTaxId(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.put(
        `${API}/companies/${companyId}`,
        { state_tax_id: stateTaxIdValue.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompany(response.data);
      setEditingStateTaxId(false);
    } catch (err) {
      console.error('Error updating State Tax ID:', err);
      alert('Failed to update State Tax ID');
    } finally {
      setSavingStateTaxId(false);
    }
  };

  const handleStateTaxIdCancel = () => {
    setStateTaxIdValue(company?.state_tax_id || '');
    setEditingStateTaxId(false);
  };

  // Tax identifiers (Sales Tax / VAT / GST) — replace-all save
  const saveTaxIdentifiers = async (nextList) => {
    try {
      setSavingTaxIds(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.put(
        `${API}/companies/${companyId}`,
        { tax_identifiers: nextList },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompany(response.data);
    } catch (err) {
      console.error('Error updating tax identifiers:', err);
      alert('Failed to update tax identifiers');
    } finally {
      setSavingTaxIds(false);
    }
  };

  const handleAddTaxIdentifier = () => {
    const list = [...(company?.tax_identifiers || []), { type: 'sales_tax', label: '', value: '', jurisdiction: '' }];
    saveTaxIdentifiers(list);
  };

  const handleTaxIdentifierChange = (index, field, value) => {
    const list = (company?.tax_identifiers || []).map((t, i) => i === index ? { ...t, [field]: value } : t);
    setCompany({ ...company, tax_identifiers: list });
  };

  const handleRemoveTaxIdentifier = (index) => {
    const list = (company?.tax_identifiers || []).filter((_, i) => i !== index);
    saveTaxIdentifiers(list);
  };

  // Date of Formation inline edit (admin + super_admin)
  const handleDofSave = async () => {
    try {
      setSavingDof(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.put(
        `${API}/companies/${companyId}`,
        { date_of_formation: dofValue || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompany(response.data);
      setEditingDof(false);
    } catch (err) {
      console.error('Error updating Date of Formation:', err);
      alert('Failed to update Date of Formation');
    } finally {
      setSavingDof(false);
    }
  };

  const handleDofCancel = () => {
    setDofValue((company?.date_of_formation || '').slice(0, 10));
    setEditingDof(false);
  };

  // BOIR Filing Required toggle
  const handleBoirStatusSave = async () => {
    if (boirRequired && !boirReason.trim()) {
      alert('Please provide a reason when enabling BOIR filing requirement');
      return;
    }
    try {
      setSavingBoirStatus(true);
      const token = localStorage.getItem('auth_token');
      await axios.patch(
        `${API}/companies/${companyId}/boir-status`,
        { 
          boir_filing_required: boirRequired, 
          boir_filing_reason: boirRequired ? boirReason.trim() : null 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingBoirStatus(false);
      fetchCompanyData();
    } catch (err) {
      console.error('Error updating BOIR status:', err);
      alert(err.response?.data?.detail || 'Failed to update BOIR filing status');
    } finally {
      setSavingBoirStatus(false);
    }
  };

  const handleBoirStatusCancel = () => {
    setBoirRequired(company?.boir_filing_required || false);
    setBoirReason(company?.boir_filing_reason || '');
    setEditingBoirStatus(false);
  };

  // BOIR Filing Document Upload
  const handleBoirFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.includes('pdf')) {
      setUploadError('Only PDF files are accepted for BOIR filing');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File size must be under 10MB');
      return;
    }

    setUploadingBoir(true);
    setUploadError('');
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(
        `${API}/companies/${companyId}/boir-filing-document`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      fetchCompanyData();
    } catch (err) {
      console.error('Error uploading BOIR document:', err);
      setUploadError(err.response?.data?.detail || 'Failed to upload BOIR filing document');
    } finally {
      setUploadingBoir(false);
      if (boirFileRef.current) boirFileRef.current.value = '';
    }
  };

  // View BO ID Document
  const handleViewBoIdDoc = async (ownerId) => {
    setViewingDocId(ownerId);
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await axios.get(
        `${API}/beneficial-owners/${ownerId}/id-document`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const url = resp.data.presigned_url || resp.data.url;
      if (url) {
        window.open(url, '_blank');
      } else {
        alert('No download URL returned');
      }
    } catch (err) {
      console.error('Error fetching BO ID doc:', err);
      alert(err.response?.data?.detail || 'Failed to load ID document');
    } finally {
      setViewingDocId(null);
    }
  };

  // Company action handlers
  const handleDeactivate = async () => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `${API}/admin/companies/${companyId}/deactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowDeactivateModal(false);
      fetchCompanyData(); // Refresh data
    } catch (err) {
      console.error('Error deactivating company:', err);
      alert(err.response?.data?.detail || 'Failed to deactivate company');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `${API}/admin/companies/${companyId}/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCompanyData(); // Refresh data
    } catch (err) {
      console.error('Error reactivating company:', err);
      alert(err.response?.data?.detail || 'Failed to reactivate company');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmText !== company?.company_name) {
      alert('Please type the company name exactly to confirm deletion');
      return;
    }
    try {
      setActionLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.delete(
        `${API}/admin/companies/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowDeleteModal(false);
      alert(response.data.message);
      navigate('/superadmin/companies');
    } catch (err) {
      console.error('Error deleting company:', err);
      alert(err.response?.data?.detail || 'Failed to delete company');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAddress = (address) => {
    if (!address) return '-';
    const parts = [
      address.street,
      address.city,
      address.state,
      address.zip_code
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '-';
  };

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 text-[#00E87A] animate-spin" />
        </div>
      </SuperAdminLayout>
    );
  }

  if (error || !company) {
    return (
      <SuperAdminLayout>
        <div className="p-6">
          <Link 
            to="/superadmin/companies" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Companies
          </Link>
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-400" />
            <p className="text-red-400">{error || 'Company not found'}</p>
          </div>
        </div>
      </SuperAdminLayout>
    );
  }

  // Render Company Info Tab
  const renderCompanyInfo = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Basic Information */}
      <div className="bg-gray-800/30 border border-gray-700/50 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#00E87A]" />
          Basic Information
        </h2>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider">Company ID</label>
            <p className="text-gray-300 mt-1 font-mono text-sm">{company.id}</p>
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider">Federal Tax ID (EIN)</label>
            <div className="mt-1 flex items-center gap-2">
              {editingEin ? (
                <>
                  <input
                    type="text"
                    value={einValue}
                    onChange={(e) => setEinValue(e.target.value)}
                    placeholder="12-3456789"
                    disabled={savingEin}
                    autoFocus
                    className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-white w-40 disabled:opacity-50 focus:outline-none focus:border-[#00E87A] font-mono"
                    data-testid="ein-input"
                  />
                  <button
                    onClick={handleEinSave}
                    disabled={savingEin}
                    className="text-[#00E87A] hover:text-[#00D46E] disabled:opacity-50"
                    data-testid="ein-save-btn"
                    title="Save"
                  >
                    {savingEin ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={handleEinCancel}
                    disabled={savingEin}
                    className="text-gray-400 hover:text-white disabled:opacity-50"
                    data-testid="ein-cancel-btn"
                    title="Cancel"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-300 font-mono text-sm">{company.ein || '-'}</p>
                  {(user?.role === 'admin' || user?.role === 'super_admin') && (
                    <button
                      onClick={() => { setEinValue(company.ein || ''); setEditingEin(true); }}
                      className="text-gray-400 hover:text-[#00E87A]"
                      data-testid="ein-edit-btn"
                      title="Edit EIN"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider">State Tax ID</label>
            <div className="mt-1 flex items-center gap-2">
              {editingStateTaxId ? (
                <>
                  <input
                    type="text"
                    value={stateTaxIdValue}
                    onChange={(e) => setStateTaxIdValue(e.target.value)}
                    placeholder="State tax registration no."
                    disabled={savingStateTaxId}
                    autoFocus
                    className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-white w-48 disabled:opacity-50 focus:outline-none focus:border-[#00E87A] font-mono"
                    data-testid="state-tax-id-input"
                  />
                  <button onClick={handleStateTaxIdSave} disabled={savingStateTaxId} className="text-[#00E87A] hover:text-[#00D46E] disabled:opacity-50" data-testid="state-tax-id-save-btn" title="Save">
                    {savingStateTaxId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  </button>
                  <button onClick={handleStateTaxIdCancel} disabled={savingStateTaxId} className="text-gray-400 hover:text-white disabled:opacity-50" data-testid="state-tax-id-cancel-btn" title="Cancel">
                    <X className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-300 font-mono text-sm">{company.state_tax_id || '-'}</p>
                  {(user?.role === 'admin' || user?.role === 'super_admin') && (
                    <button onClick={() => { setStateTaxIdValue(company.state_tax_id || ''); setEditingStateTaxId(true); }} className="text-gray-400 hover:text-[#00E87A]" data-testid="state-tax-id-edit-btn" title="Edit State Tax ID">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs text-gray-500 uppercase tracking-wider">Tax Identifiers (Sales Tax / VAT / GST)</label>
              {(user?.role === 'admin' || user?.role === 'super_admin') && (
                <button onClick={handleAddTaxIdentifier} disabled={savingTaxIds} className="text-xs text-[#00E87A] hover:text-[#00D46E] disabled:opacity-50 flex items-center gap-1" data-testid="add-tax-identifier-btn">
                  <Plus className="w-3.5 h-3.5" /> Add
                </button>
              )}
            </div>
            <div className="mt-2 space-y-2">
              {(company.tax_identifiers || []).length === 0 ? (
                <p className="text-gray-500 text-sm">None added</p>
              ) : (
                (company.tax_identifiers || []).map((t, index) => {
                  const canEdit = user?.role === 'admin' || user?.role === 'super_admin';
                  return (
                    <div key={index} className="flex flex-wrap items-center gap-2 bg-gray-900 border border-gray-700 rounded px-2 py-2" data-testid={`tax-identifier-row-${index}`}>
                      {canEdit ? (
                        <>
                          <select value={t.type || 'sales_tax'} onChange={(e) => handleTaxIdentifierChange(index, 'type', e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-[#00E87A]">
                            <option value="sales_tax">Sales Tax</option>
                            <option value="vat">VAT</option>
                            <option value="gst">GST</option>
                            <option value="other">Other</option>
                          </select>
                          <input type="text" value={t.jurisdiction || ''} onChange={(e) => handleTaxIdentifierChange(index, 'jurisdiction', e.target.value)} placeholder="Jurisdiction" className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white w-28 focus:outline-none focus:border-[#00E87A]" />
                          <input type="text" value={t.value || ''} onChange={(e) => handleTaxIdentifierChange(index, 'value', e.target.value)} placeholder="Number" className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white w-40 font-mono focus:outline-none focus:border-[#00E87A]" />
                          <button onClick={() => saveTaxIdentifiers(company.tax_identifiers)} disabled={savingTaxIds} className="text-[#00E87A] hover:text-[#00D46E] disabled:opacity-50" title="Save" data-testid={`tax-identifier-save-${index}`}>
                            {savingTaxIds ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          </button>
                          <button onClick={() => handleRemoveTaxIdentifier(index)} disabled={savingTaxIds} className="text-red-400 hover:text-red-300 disabled:opacity-50" title="Remove" data-testid={`tax-identifier-remove-${index}`}>
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <span className="text-gray-300 text-sm">
                          <span className="uppercase text-gray-500 text-xs mr-2">{t.type}</span>
                          {t.jurisdiction ? `${t.jurisdiction}: ` : ''}<span className="font-mono">{t.value}</span>
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider">Country</label>
            <p className="text-gray-300 mt-1 flex items-center gap-2">
              <Globe className="w-4 h-4 text-gray-500" />
              {company.country || 'US'}
            </p>
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider">Date of Formation</label>
            <div className="mt-1 flex items-center gap-2">
              {editingDof ? (
                <>
                  <input
                    type="date"
                    value={dofValue}
                    onChange={(e) => setDofValue(e.target.value)}
                    disabled={savingDof}
                    autoFocus
                    className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-white disabled:opacity-50 focus:outline-none focus:border-[#00E87A]"
                    data-testid="dof-input"
                  />
                  <button
                    onClick={handleDofSave}
                    disabled={savingDof}
                    className="text-[#00E87A] hover:text-[#00D46E] disabled:opacity-50"
                    data-testid="dof-save-btn"
                    title="Save"
                  >
                    {savingDof ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={handleDofCancel}
                    disabled={savingDof}
                    className="text-gray-400 hover:text-white disabled:opacity-50"
                    data-testid="dof-cancel-btn"
                    title="Cancel"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-300">{formatLongDate(company.date_of_formation)}</p>
                  {(user?.role === 'admin' || user?.role === 'super_admin') && (
                    <button
                      onClick={() => { setDofValue((company.date_of_formation || '').slice(0, 10)); setEditingDof(true); }}
                      className="text-gray-400 hover:text-[#00E87A]"
                      data-testid="dof-edit-btn"
                      title="Edit Date of Formation"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider">BOIR Filed</label>
            <div className="mt-1 flex items-center gap-2">
              {editingBoir ? (
                <>
                  <select
                    value={boirValue ? 'yes' : 'no'}
                    onChange={(e) => setBoirValue(e.target.value === 'yes')}
                    className="bg-gray-700 border border-gray-600 text-white text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#00E87A]"
                    data-testid="boir-select"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                  <button
                    onClick={handleBoirSave}
                    disabled={savingBoir}
                    className="p-1.5 bg-[#00E87A]/20 text-[#00E87A] rounded-lg hover:bg-[#00E87A]/30 transition-colors disabled:opacity-50"
                    data-testid="boir-save-btn"
                  >
                    {savingBoir ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={handleBoirCancel}
                    disabled={savingBoir}
                    className="p-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors disabled:opacity-50"
                    data-testid="boir-cancel-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  {company.boir_filed ? (
                    <span className="text-green-400">Yes{company.boir_filing_date ? ` - ${formatDate(company.boir_filing_date)}` : ''}</span>
                  ) : (
                    <span className="text-yellow-400">No</span>
                  )}
                  {user?.role === 'super_admin' && (
                    <button
                      onClick={() => setEditingBoir(true)}
                      className="p-1.5 bg-gray-700/50 text-gray-400 rounded-lg hover:bg-gray-700 hover:text-white transition-colors"
                      data-testid="boir-edit-btn"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* BOIR Filing Management */}
      <div className="bg-gray-800/30 border border-gray-700/50 rounded-xl p-6" data-testid="boir-filing-section">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#00E87A]" />
          BOIR Filing Management
        </h2>

        <div className="grid grid-cols-1 gap-6">
          {/* Filing Required Toggle */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs text-gray-500 uppercase tracking-wider">Filing Required</label>
              {!editingBoirStatus && (
                <button
                  onClick={() => setEditingBoirStatus(true)}
                  className="p-1 text-gray-500 hover:text-white transition-colors"
                  data-testid="boir-status-edit-btn"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {editingBoirStatus ? (
              <div className="space-y-3 p-3 bg-[#0D1117] rounded-lg border border-gray-700">
                <label className="flex items-center gap-3 cursor-pointer">
                  <button
                    type="button"
                    onClick={() => setBoirRequired(!boirRequired)}
                    className="flex-shrink-0"
                    data-testid="boir-required-toggle"
                  >
                    {boirRequired ? (
                      <ToggleRight className="w-8 h-8 text-[#00E87A]" />
                    ) : (
                      <ToggleLeft className="w-8 h-8 text-gray-500" />
                    )}
                  </button>
                  <span className="text-sm text-gray-300">
                    {boirRequired ? 'Required' : 'Not Required'}
                  </span>
                </label>
                {boirRequired && (
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Reason *</label>
                    <input
                      type="text"
                      value={boirReason}
                      onChange={(e) => setBoirReason(e.target.value)}
                      placeholder="e.g. Foreign-owned entity"
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-[#00E87A]"
                      data-testid="boir-reason-input"
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={handleBoirStatusSave}
                    disabled={savingBoirStatus}
                    className="px-3 py-1.5 bg-[#00E87A] text-black text-sm font-medium rounded-lg hover:bg-[#00D46E] disabled:opacity-50 flex items-center gap-1"
                    data-testid="boir-status-save-btn"
                  >
                    {savingBoirStatus ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    Save
                  </button>
                  <button
                    onClick={handleBoirStatusCancel}
                    className="px-3 py-1.5 bg-gray-700 text-gray-300 text-sm rounded-lg hover:bg-gray-600"
                    data-testid="boir-status-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {company.boir_filing_required ? (
                  <span className="px-2.5 py-1 bg-amber-500/20 text-amber-400 text-sm rounded-lg flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Required
                  </span>
                ) : (
                  <span className="px-2.5 py-1 bg-gray-600/30 text-gray-400 text-sm rounded-lg">
                    Not Required
                  </span>
                )}
                {company.boir_filing_reason && (
                  <span className="text-gray-500 text-xs">({company.boir_filing_reason})</span>
                )}
              </div>
            )}
          </div>

          {/* Filing Status & Upload */}
          <div className="space-y-3">
            <label className="text-xs text-gray-500 uppercase tracking-wider block">Filing Status</label>
            
            {company.boir_filed ? (
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Check className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 font-medium text-sm">BOIR Filed</span>
                </div>
                {company.boir_filing_date && (
                  <p className="text-gray-400 text-xs">Filed on {formatDate(company.boir_filing_date)}</p>
                )}
                {company.boir_filing_document_id && (
                  <p className="text-gray-500 text-xs mt-1">Document saved to company vault</p>
                )}
              </div>
            ) : (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="w-4 h-4 text-yellow-400" />
                  <span className="text-yellow-400 font-medium text-sm">Not Filed</span>
                </div>
                <p className="text-gray-400 text-xs">Upload a BOIR filing receipt to mark as filed</p>
              </div>
            )}

            {/* Upload Button */}
            <div>
              <input
                ref={boirFileRef}
                type="file"
                accept=".pdf"
                onChange={handleBoirFileUpload}
                className="hidden"
                data-testid="boir-file-input"
              />
              <button
                onClick={() => boirFileRef.current?.click()}
                disabled={uploadingBoir}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0D1117] border border-dashed border-gray-600 rounded-lg text-gray-300 text-sm hover:border-[#00E87A] hover:text-[#00E87A] transition-colors disabled:opacity-50"
                data-testid="boir-upload-btn"
              >
                {uploadingBoir ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4" /> {company.boir_filed ? 'Replace BOIR Filing Receipt (PDF)' : 'Upload BOIR Filing Receipt (PDF)'}</>
                )}
              </button>
              {uploadError && (
                <p className="text-red-400 text-xs mt-1">{uploadError}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Formation Documents — only for self-registered companies */}
      {company?.registration_type === 'self_registered' && (
        <FormationDocumentsSection
          companyId={companyId}
          token={localStorage.getItem('auth_token')}
        />
      )}

      {/* Service Dates — super admin only */}
      {isSuperAdmin && (
        <div className="lg:col-span-2 bg-gray-800/30 border border-gray-700/50 rounded-xl p-6" data-testid="service-dates-section">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#00E87A]" />
            Service Dates
          </h2>

          {serviceDateSuccess && (
            <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm flex items-center gap-2" data-testid="service-date-success">
              <CheckCircle className="w-4 h-4" /> {serviceDateSuccess}
            </div>
          )}
          {serviceDateError && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm flex items-center gap-2" data-testid="service-date-error">
              <AlertCircle className="w-4 h-4" /> {serviceDateError}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Mail Forwarding */}
            <ServiceDateCard
              label="Mail Forwarding"
              status={company?.mail_forwarding_status}
              expiry={company?.mail_forwarding_expiry}
              source={company?.mail_forwarding_source}
              locked={company?.mail_forwarding_due_date_locked}
              dateValue={mfDueDate}
              onDateChange={setMfDueDate}
              onSave={() => handleSaveServiceDate('mail_forwarding')}
              saving={savingMf}
              testId="mf"
            />
            {/* Registered Agent */}
            <ServiceDateCard
              label="Registered Agent"
              status={company?.registered_agent_status}
              expiry={company?.registered_agent_expiry}
              source={company?.registered_agent_source}
              locked={company?.registered_agent_due_date_locked}
              dateValue={raDueDate}
              onDateChange={setRaDueDate}
              onSave={() => handleSaveServiceDate('registered_agent')}
              saving={savingRa}
              testId="ra"
            />
          </div>
        </div>
      )}

      {/* State Filing — admin visibility (Stage 4) */}
      {stateFilingLoaded && (
        <div className="bg-[#0D1117] border border-gray-700/50 rounded-xl p-6 mt-6" data-testid="admin-state-filing-section">
          <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <CalendarDays className="w-5 h-5 text-[#00E87A]" />
              <div>
                <h3 className="text-lg font-semibold text-white">State Filing</h3>
                {stateFiling ? (
                  <p className="text-sm text-gray-400 mt-0.5">
                    {stateFiling.state_name} {stateFiling.filing_name}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500 mt-0.5">
                    State filing tracking not yet available for this state
                  </p>
                )}
              </div>
            </div>
            {stateFiling && (
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  {
                    green:  'bg-[#00E87A]/20 text-[#00E87A]',
                    red:    'bg-red-500/20 text-red-400',
                    orange: 'bg-orange-500/20 text-orange-400',
                    amber:  'bg-amber-500/20 text-amber-400',
                    grey:   'bg-gray-600/30 text-gray-300',
                  }[stateFiling.status?.tone] || 'bg-gray-600/30 text-gray-300'
                }`}
                data-testid="admin-state-filing-status"
              >
                {stateFiling.status?.label}
              </span>
            )}
          </div>

          {stateFiling && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-500">Due Date</p>
                  <p className="text-white" data-testid="admin-state-filing-due-date">
                    {stateFiling.due_date ? formatLongDate(stateFiling.due_date) : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Frequency</p>
                  <p className="text-white">
                    {stateFiling.frequency_years > 1
                      ? `Every ${stateFiling.frequency_years} years`
                      : 'Annual'}
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-800 pt-4 space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-gray-400">Government Fee</span>
                  {stateFiling.fee_type === 'variable' ? (
                    <span className="text-gray-300 text-right max-w-[60%]">
                      {stateFiling.govt_fee_note || 'Calculated before filing'}
                    </span>
                  ) : (
                    <span className="text-white font-mono">
                      ${Number(stateFiling.govt_fee).toFixed(2)} <span className="text-gray-500 text-xs">(Fixed)</span>
                    </span>
                  )}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Platform Fee</span>
                  <span className="text-white font-mono">
                    ${Number(stateFiling.platform_fee).toFixed(2)}
                  </span>
                </div>
                {stateFiling.fee_type === 'fixed' && (
                  <div className="flex justify-between pt-2 border-t border-gray-800">
                    <span className="text-gray-300 font-medium">Total</span>
                    <span className="text-white font-mono font-semibold">
                      ${Number(stateFiling.total_amount).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-5 flex items-center gap-3 flex-wrap">
                {stateFiling.has_active_order && stateFiling.active_order_status === 'payment_pending' && (
                  <button
                    onClick={handleSendStateFilingReminder}
                    disabled={sendingReminder}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-gray-200 border border-gray-700 hover:bg-gray-800 disabled:opacity-50"
                    data-testid="send-state-filing-reminder-btn"
                  >
                    <Mail className="w-4 h-4" />
                    {sendingReminder ? 'Sending…' : 'Send Reminder'}
                  </button>
                )}
                {reminderMessage && (
                  <span className="text-sm text-gray-400" data-testid="state-filing-reminder-msg">
                    {reminderMessage}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );


  const isIntlCompany = !!(company?.country && company.country !== 'US');
  const foreignOwners = Array.isArray(company?.owners) ? company.owners : [];
  const usesOwnersArray = isIntlCompany || foreignOwners.length > 0;
  const foreignBOs = foreignOwners.filter(o => {
    const roles = Array.isArray(o.roles) ? o.roles : (o.role ? [o.role] : []);
    return roles.some(r => (r || '').toLowerCase() === 'beneficial owner');
  });
  const downloadForeignOwnerDoc = async (index) => {
    try {
      const res = await axios.get(`${API}/companies/${companyId}/owners/${index}/document`, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });
      if (res.data.url) window.open(res.data.url, '_blank', 'noopener');
    } catch (e) { alert('Unable to open the document.'); }
  };
  const renderForeignOwnerList = (list, emptyMsg) => (
    list.length === 0 ? (
      <div className="bg-[#0D1117] border border-gray-700/50 rounded-xl p-12 text-center">
        <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400 text-lg">{emptyMsg}</p>
      </div>
    ) : (
      <div className="space-y-3" data-testid="intl-admin-owner-list">
        {list.map((o, i) => {
          const origIndex = foreignOwners.indexOf(o);
          const roles = Array.isArray(o.roles) ? o.roles : (o.role ? [o.role] : []);
          return (
            <div key={i} className="bg-[#0D1117] border border-gray-700/50 rounded-xl p-4" data-testid={`intl-admin-owner-${origIndex}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-white font-medium">{o.full_name}</p>
                  <p className="text-gray-500 text-xs mt-0.5">{o.owner_type === 'company' ? 'Company' : 'Individual'} · {roles.join(', ') || '-'}</p>
                  <div className="flex gap-4 mt-2 text-sm">
                    <span className="text-gray-400">Ownership: <span className="text-white">{o.ownership_percentage != null ? `${o.ownership_percentage}%` : '-'}</span></span>
                    <span className="text-gray-400">Nationality/Country: <span className="text-white">{o.nationality || '-'}</span></span>
                    <span className="text-gray-400">ID/Reg No: <span className="text-white font-mono">{o.id_number || '-'}</span></span>
                  </div>
                </div>
                {(o.document_url || o.document_s3_key) && (
                  <button onClick={() => downloadForeignOwnerDoc(origIndex)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-[#00E87A]/15 text-[#00E87A] hover:bg-[#00E87A]/25" data-testid={`intl-admin-owner-doc-${origIndex}`}>
                    <Download className="w-4 h-4" /> {o.document_type === 'formation_certificate' ? 'Certificate' : 'Passport'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    )
  );

  // Render Members Tab
  const renderMembersTab = () => (
    <div data-testid="members-tab-content">
      {usesOwnersArray ? (
        renderForeignOwnerList(foreignOwners, 'No members added yet')
      ) : members.length === 0 ? (
        <div className="bg-[#0D1117] border border-gray-700/50 rounded-xl p-12 text-center">
          <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg" data-testid="no-members-message">No members added yet</p>
          <p className="text-gray-500 text-sm mt-2">Members will appear here once added by the client.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-gray-400 text-sm">
              Showing {members.length} member{members.length !== 1 ? 's' : ''}
            </p>
          </div>
          {members.map((member) => (
            <MemberCard
              key={member.id}
              member={member}
              formatDate={formatDate}
              companyId={companyId}
              token={localStorage.getItem('auth_token')}
              onUpdate={fetchCompanyData}
              entityType={company?.entity_type}
            />
          ))}
        </div>
      )}
    </div>
  );

  // Render Owners Tab — enhanced with BO details and admin actions
  const renderOwnersTab = () => (
    <div data-testid="owners-tab-content">
      {usesOwnersArray ? (
        renderForeignOwnerList(foreignBOs, 'No beneficial owners found for this company')
      ) : (
      <>
      {/* Info banner */}
      <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-start gap-2">
        <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
        <p className="text-blue-300 text-xs leading-relaxed">
          Beneficial owners reported under the Corporate Transparency Act. Admin can view uploaded ID documents for verification.
        </p>
      </div>

      {owners.length === 0 ? (
        <div className="bg-[#0D1117] border border-gray-700/50 rounded-xl p-12 text-center">
          <UserCheck className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No beneficial owners found for this company</p>
          <p className="text-gray-500 text-sm mt-2">Beneficial owners will appear here once added by the client.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-400 text-sm">
              {owners.length} beneficial owner{owners.length !== 1 ? 's' : ''}
            </p>
          </div>
          {owners.map((bo) => (
            <div key={bo.id} className="bg-[#0D1117] border border-gray-700/50 rounded-xl p-5 hover:border-[#00E87A]/20 transition-colors" data-testid={`bo-card-${bo.id}`}>
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                    <User className="w-5 h-5 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{bo.full_legal_name || bo.full_name || '-'}</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      {bo.member_id && (
                        <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 text-[10px] font-medium rounded">Also a Member</span>
                      )}
                      {bo.email && (
                        <span className="text-gray-500 text-xs">{bo.email}</span>
                      )}
                    </div>
                  </div>
                </div>
                {/* View ID Doc button */}
                {bo.id_document_s3_key && (
                  <button
                    onClick={() => handleViewBoIdDoc(bo.id)}
                    disabled={viewingDocId === bo.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700/50 text-gray-300 text-xs rounded-lg hover:bg-gray-700 hover:text-white transition-colors disabled:opacity-50"
                    data-testid={`view-id-doc-${bo.id}`}
                  >
                    {viewingDocId === bo.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Eye className="w-3 h-3" />
                    )}
                    View ID Doc
                  </button>
                )}
              </div>

              {/* Details grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-3 border-t border-gray-700/50">
                <div>
                  <label className="text-[10px] text-gray-500 uppercase tracking-wider block">Ownership %</label>
                  <p className="text-gray-300 text-sm mt-0.5">
                    {bo.ownership_percentage ? `${bo.ownership_percentage}%` : '—'}
                  </p>
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 uppercase tracking-wider block">Date of Birth</label>
                  <p className="text-gray-300 text-sm mt-0.5">{bo.date_of_birth || '—'}</p>
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 uppercase tracking-wider block">Substantial Control</label>
                  <p className="text-gray-300 text-sm mt-0.5">
                    {bo.has_substantial_control ? (
                      <span className="text-[#00E87A]">{(bo.control_basis || 'Yes').replace(/_/g, ' ')}</span>
                    ) : '—'}
                  </p>
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 uppercase tracking-wider block">ID Document</label>
                  <p className="text-gray-300 text-sm mt-0.5">
                    {bo.id_document_type ? (
                      <span>{bo.id_document_type.replace(/_/g, ' ')} (****{bo.id_document_number_last4 || ''})</span>
                    ) : '—'}
                  </p>
                </div>
              </div>

              {/* Address */}
              {bo.residential_address && (bo.residential_address.street || bo.residential_address.city) && (
                <div className="pt-3 border-t border-gray-700/50">
                  <label className="text-[10px] text-gray-500 uppercase tracking-wider block mb-0.5">Residential Address</label>
                  <p className="text-gray-400 text-sm">
                    {[
                      bo.residential_address.street,
                      bo.residential_address.city,
                      bo.residential_address.state,
                      bo.residential_address.postal,
                      bo.residential_address.country
                    ].filter(Boolean).join(', ')}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </>
      )}

    </div>
  );


  return (
    <SuperAdminLayout>
      <div className="p-6" data-testid="super-admin-company-detail">
        {/* Back Link */}
        <Link 
          to="/superadmin/companies" 
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          data-testid="back-to-companies"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Companies
        </Link>

        {/* Company Header */}
        <div className="bg-gray-800/30 border border-gray-700/50 rounded-xl p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-[#00E87A]/10 flex items-center justify-center">
                <Building2 className="w-7 h-7 text-[#00E87A]" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-white" data-testid="company-name">
                    {company.company_name}
                  </h1>
                  {/* Status Badges */}
                  {company.is_deleted && (
                    <span className="px-2.5 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded-full flex items-center gap-1">
                      <Trash2 className="w-3 h-3" />
                      DELETED
                    </span>
                  )}
                  {!company.is_deleted && !company.is_active && (
                    <span className="px-2.5 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-medium rounded-full flex items-center gap-1">
                      <PowerOff className="w-3 h-3" />
                      INACTIVE
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  <span className="px-3 py-1 bg-gray-700/50 text-gray-300 text-sm rounded-md">
                    {ENTITY_TYPE_LABELS[company.entity_type] || company.entity_type}
                  </span>
                  <span className="flex items-center gap-1.5 text-gray-400 text-sm">
                    <MapPin className="w-4 h-4" />
                    {company.state_of_formation || '-'}
                  </span>
                  <span className="flex items-center gap-1.5 text-gray-400 text-sm">
                    <Calendar className="w-4 h-4" />
                    Created {formatDate(company.created_at)}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Action Buttons - Super Admin Only */}
            {user?.role === 'super_admin' && !company.is_deleted && (
              <div className="flex items-center gap-2">
                {company.is_active ? (
                  <button
                    onClick={() => setShowDeactivateModal(true)}
                    disabled={actionLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors disabled:opacity-50"
                    data-testid="deactivate-btn"
                  >
                    <PowerOff className="w-4 h-4" />
                    Deactivate
                  </button>
                ) : (
                  <button
                    onClick={handleReactivate}
                    disabled={actionLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-[#00E87A]/20 text-[#00E87A] rounded-lg hover:bg-[#00E87A]/30 transition-colors disabled:opacity-50"
                    data-testid="reactivate-btn"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
                    Reactivate
                  </button>
                )}
                <button
                  onClick={() => setShowDeleteModal(true)}
                  disabled={actionLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors disabled:opacity-50"
                  data-testid="delete-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 border-b border-gray-700/50 pb-2 overflow-x-auto">
          {[...BASE_TABS, ...(activeMailServices.length > 0 ? [{ id: 'mail_scans', label: 'Mail Scans', icon: Mail }] : [])].map((tab) => {
            const Icon = tab.icon;
            const count = tab.id === 'members'
              ? (usesOwnersArray ? foreignOwners.length : members.length)
              : tab.id === 'owners'
                ? (usesOwnersArray ? foreignBOs.length : owners.length)
                : null;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#00E87A]/10 text-[#00E87A] border border-[#00E87A]/30'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {count !== null && (
                  <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.id ? 'bg-[#00E87A]/20' : 'bg-gray-700'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]">
          {activeTab === 'info' && renderCompanyInfo()}
          {activeTab === 'members' && renderMembersTab()}
          {activeTab === 'owners' && renderOwnersTab()}
          {activeTab === 'docvault' && (
            <AdminDocVaultTab companyId={companyId} companyName={company?.company_name} />
          )}
          {activeTab === 'contacts' && (
            <AdminContactsTab
              companyId={companyId}
              companyName={company?.company_name}
              onOpenDocRoom={() => setActiveTab('docroom')}
            />
          )}
          {activeTab === 'docroom' && (
            <AdminDocumentRoomTab companyId={companyId} companyName={company?.company_name} />
          )}
          {activeTab === 'mail_scans' && (
            <MailScansSection
              companyId={companyId}
              activeServices={activeMailServices}
              companyName={company?.company_name}
            />
          )}
          {activeTab === 'orders' && (
            <div className="space-y-4" data-testid="company-orders-tab">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Orders for this company</h3>
                <span className="text-sm text-gray-400">{orders.length} order{orders.length === 1 ? '' : 's'}</span>
              </div>

              {ordersLoading ? (
                <div className="text-center py-12 text-gray-400">Loading…</div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12 bg-[#161B22] border border-[#21262D] rounded-lg" data-testid="company-orders-empty">
                  <ShoppingBag className="w-10 h-10 mx-auto text-gray-600 mb-3" />
                  <p className="text-gray-400">No orders for this company yet.</p>
                </div>
              ) : (
                <div className="bg-[#161B22] border border-[#21262D] rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[#21262D] bg-[#0D1117]">
                          <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Order ID</th>
                          <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Client</th>
                          <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">Service</th>
                          <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Amount</th>
                          <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Status</th>
                          <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">Date</th>
                          <th className="text-right text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((o) => {
                          const ss = getStatusStyle(o.status);
                          const amt = o.amount_paid ?? o.amount ?? o.price;
                          const fmtAmt = (amt === null || amt === undefined)
                            ? '-'
                            : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amt);
                          return (
                            <tr key={o.id} className="border-b border-[#21262D] hover:bg-white/[0.02]">
                              <td className="px-4 py-3 text-sm font-mono text-gray-300 truncate" style={{ maxWidth: '140px' }}>
                                {o.id ? `${o.id.slice(0, 12)}…` : '-'}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <div className="text-white">{o.user_name || '-'}</div>
                                <div className="text-gray-500 text-xs">{o.user_email || ''}</div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-300 hidden md:table-cell">
                                {o.product_name || o.service_name || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-white">{fmtAmt}</td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${ss.bg} ${ss.text}`}>
                                  {ss.label}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-400 hidden md:table-cell">
                                {o.created_at ? new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <Link to={`/superadmin/orders/${o.id}`} className="text-[#00E87A] hover:text-[#00C66A] text-sm font-medium" data-testid={`view-order-${o.id}`}>
                                  View →
                                </Link>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Deactivate Confirmation Modal */}
        {showDeactivateModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-[#161B22] border border-gray-700 rounded-xl p-6 max-w-md w-full mx-4" data-testid="deactivate-modal">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Deactivate Company</h3>
              </div>
              <p className="text-gray-400 mb-6">
                Are you sure you want to deactivate <span className="text-white font-medium">{company.company_name}</span>? 
                The company will be hidden from the client but can be reactivated later.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeactivateModal(false)}
                  className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                  data-testid="deactivate-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeactivate}
                  disabled={actionLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black font-medium rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-50"
                  data-testid="deactivate-confirm-btn"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <PowerOff className="w-4 h-4" />}
                  Deactivate
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-[#161B22] border border-gray-700 rounded-xl p-6 max-w-md w-full mx-4" data-testid="delete-modal">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Delete Company</h3>
              </div>
              <p className="text-gray-400 mb-4">
                This will permanently hide <span className="text-white font-medium">{company.company_name}</span> from all users except super admins. 
                All active orders will be cancelled. Data is preserved but not recoverable by the client.
              </p>
              <div className="mb-6">
                <label className="text-sm text-gray-400 block mb-2">
                  Type <span className="text-red-400 font-mono">{company.company_name}</span> to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type company name..."
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                  data-testid="delete-confirm-input"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmText('');
                  }}
                  className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                  data-testid="delete-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={actionLoading || deleteConfirmText !== company.company_name}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="delete-confirm-btn"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Delete Company
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminCompanyDetail;
